import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
}

/**
 * 🛡️ ERROR BOUNDARY para capturar errores de React
 * Previene que errores en componentes hijos crasheen toda la aplicación
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log del error para debugging
        if (import.meta.env.DEV) {
            console.error('🚨 Error capturado por ErrorBoundary:', {
                error,
                errorInfo,
                componentStack: errorInfo.componentStack
            });
        }

        // En producción, enviar a servicio de logging
        // logErrorToService(error, errorInfo);

        this.setState({
            error,
            errorInfo
        });
    }

    render() {
        if (this.state.hasError) {
            // Mostrar UI de fallback personalizado o genérico
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                            <svg
                                className="w-6 h-6 text-red-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-center text-gray-800 mb-2">
                            Algo salió mal
                        </h2>
                        <p className="text-gray-600 text-center mb-4">
                            Ocurrió un error inesperado. Por favor, recarga la página.
                        </p>
                        {import.meta.env.DEV && this.state.error && (
                            <details className="mt-4 p-3 bg-gray-100 rounded text-sm">
                                <summary className="cursor-pointer font-medium text-gray-700">
                                    Detalles del error (solo en desarrollo)
                                </summary>
                                <pre className="mt-2 text-xs overflow-auto text-red-600">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full mt-4 bg-pink-500 hover:bg-pink-600 text-white font-medium py-2 px-4 rounded transition-colors"
                        >
                            Recargar página
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
